/**
 * Sunucu kodu -> CEVIRI ANAHTARI haritalari.
 *
 * DEGERLER METIN DEGIL ANAHTAR. Bu dosya modul seviyesinde yasiyor; burada t()
 * cagirmak metni IMPORT ANINDA dondurur ve dil degistiginde bayat birakir.
 * Adlarin `..._LABEL_KEYS` olmasi kullanim hatasini okurken belli etsin diye:
 * `{PERMISSION_GROUP_LABEL_KEYS[g]}` ekranda ham anahtar basar, dogrusu
 * `{t(PERMISSION_GROUP_LABEL_KEYS[g])}`. (Mobil tarafta bu tam olarak boyle
 * kirildi; ad oradan geliyor.)
 *
 * Anahtarlarin gercekten var oldugunu __tests__/labels.test.ts dogruluyor.
 */

/** Yetki grubu -> bolum basligi. Sunucudaki permission_group degerleri. */
export const PERMISSION_GROUP_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  operation: 'members.groups.operation',
  finance: 'members.groups.finance',
  report: 'members.groups.report',
  management: 'members.groups.management',
});

/** Grup sirasi — sunucu alfabetik doner, ekranda anlamli sira isteniyor. */
export const PERMISSION_GROUP_ORDER: readonly string[] = Object.freeze([
  'operation',
  'finance',
  'report',
  'management',
]);

/** Uye tipi -> rozet etiketi. */
export const MEMBER_TYPE_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  admin: 'members.types.admin',
  staff: 'members.types.staff',
});

/**
 * Uye tipinin CEVRILMIS etiketi.
 *
 * `t` disaridan aliniyor: bu dosya modul seviyesinde ve burada t() cagirmak
 * metni import aninda dondururdu. Haritayi disari acmak yerine bir fonksiyon
 * sunmanin sebebi, cagri yerinin `t()` ile sarmayi UNUTAMAMASI — ham anahtar
 * basan bir ekran, mobil tarafta tam olarak boyle olustu.
 */
export const translateMemberType = (
  t: (key: string) => string,
  memberType: string | null,
): string | null => {
  if (memberType === null) return null;
  const key = MEMBER_TYPE_LABEL_KEYS[memberType];
  return key === undefined ? null : t(key);
};

/**
 * Yetki kodu -> etiket. Katalog `descriptionTr` gonderiyor; bu harita onun
 * YEDEGI (aciklama bos gelirse ham kod gosterilmesin diye).
 */
export const PERMISSION_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  viewQuoteRequests: 'permissions.viewQuoteRequests',
  createOffer: 'permissions.createOffer',
  editOffer: 'permissions.editOffer',
  completeTask: 'permissions.completeTask',
  viewAccounts: 'permissions.viewAccounts',
  manageAccounts: 'permissions.manageAccounts',
  viewReport: 'permissions.viewReport',
  createReport: 'permissions.createReport',
});

/**
 * Denetim kaydi eylem kodu -> etiket. Sunucudaki kodlar teknik; kullaniciya
 * Turkce karsiligi gosterilir. Bilinmeyen bir kod gelirse ham hali basilir —
 * yeni bir sunucu surumu bu ekrani BOS BIRAKMAMALI.
 */
export const AUDIT_ACTION_LABEL_KEYS: Readonly<Record<string, string>> = Object.freeze({
  memberInvited: 'auditActions.memberInvited',
  invitationCancelled: 'auditActions.invitationCancelled',
  invitationAccepted: 'auditActions.invitationAccepted',
  memberProvisioned: 'auditActions.memberProvisioned',
  memberRemoved: 'auditActions.memberRemoved',
  memberDeactivated: 'auditActions.memberDeactivated',
  memberUpdated: 'auditActions.memberUpdated',
  permissionGranted: 'auditActions.permissionGranted',
  permissionRevoked: 'auditActions.permissionRevoked',
  organizationUpdated: 'auditActions.organizationUpdated',
  accountCreated: 'auditActions.accountCreated',
  accountUpdated: 'auditActions.accountUpdated',
  accountDeactivated: 'auditActions.accountDeactivated',
});

/** Filtre listesinin sirasi — Object.keys sirasina guvenilmiyor. */
export const AUDIT_ACTION_CODES: readonly string[] = Object.freeze(
  Object.keys(AUDIT_ACTION_LABEL_KEYS),
);

/**
 * Sunucu hata KODU -> kullaniciya gosterilecek metnin anahtari.
 *
 * Sunucu bu uclarda Turkce degil KOD doner ('LAST_ADMIN_PROTECTED'). Ham
 * kodu basmak (mobilin yaptigi) kullaniciya hicbir sey anlatmaz.
 */
export const SERVER_ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = Object.freeze({
  LAST_ADMIN_PROTECTED: 'org.errors.lastAdminProtected',
  CANNOT_MODIFY_OWN_MEMBERSHIP: 'org.errors.cannotModifyOwnMembership',
  MEMBER_NOT_FOUND: 'org.errors.memberNotFound',
  MEMBER_ID_REQUIRED: 'org.errors.memberNotFound',
  ADMIN_HAS_IMPLICIT_PERMISSIONS: 'org.errors.adminImplicitPermissions',
  ADMIN_PERMISSIONS_IMPLICIT: 'org.errors.adminImplicitPermissions',
  PERMISSION_NOT_GRANTABLE: 'org.errors.permissionNotGrantable',
  PERMISSION_CODES_REQUIRED: 'org.errors.permissionNotGrantable',
  EMAIL_ALREADY_REGISTERED: 'org.errors.emailAlreadyRegistered',
  INVALID_EMAIL: 'org.invite.invalidEmailDetail',
  FULL_NAME_REQUIRED: 'org.invite.fullNameRequiredDetail',
  INVALID_MEMBER_TYPE: 'org.errors.invalidMemberType',
  ORGANIZATION_NOT_FOUND: 'org.errors.organizationNotFound',
  ORGANIZATION_UPDATE_FAILED: 'org.profile.saveFailed',
  FORBIDDEN: 'org.onlyAdmins',
  ORG_CONTEXT_REQUIRED: 'org.profile.notMember',
});
